import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Card, CardBody, CardTitle, Alert } from "reactstrap";
import { Link } from "react-router-dom";
import LoaderInner from "../../components/LoaderInner";
import { StateContext } from "../../utils/context";
import { getUser } from "../../utils/getUser";
import { getCookie } from "../../jwt/_helpers/cookie";
import { stripeSubscriptionService } from "../../jwt/_services/stripeSubscription.service";
import {
  buildPlanEntitlements,
  buildBillingPortalUrl,
  enrichSubscriptionDetails,
  getActiveTenantSubscription,
  getPlanCardCta,
  getPlansBillingIntervals,
  getPlansForBillingInterval,
  getSubscriptionId,
  isPlanDowngrade,
  normalizePlanInterval,
  resolveCurrentPlan,
  userHasActiveSubscription,
} from "./planUtils";
import SubscriptionPlanCard from "./subscriptionPlanCard";
import "./tenantCustomerPlans.css";

const AvailablePlansSection = ({
  title = "Available plans",
  showManageLink = false,
  hideWhenEmpty = false,
  hideWhenSubscribed = false,
  onPlansLoaded,
}) => {
  const { currentUser, setCurrentUser } = useContext(StateContext);
  const [plans, setPlans] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inProgress, setInProgress] = useState(false);
  const [billingInProgress, setBillingInProgress] = useState(false);
  const [billingInterval, setBillingInterval] = useState("month");
  const userSelectedBillingInterval = useRef(false);

  const syncSubscriptionFromUser = (user) => {
    setSubscriptionDetails(getActiveTenantSubscription(user));
  };

  const activeSubscription = useMemo(
    () => enrichSubscriptionDetails(subscriptionDetails, plans),
    [subscriptionDetails, plans]
  );

  const currentPlan = useMemo(
    () => resolveCurrentPlan(plans, subscriptionDetails),
    [plans, subscriptionDetails]
  );

  const hasActiveSubscription = useMemo(
    () => userHasActiveSubscription(plans, subscriptionDetails),
    [plans, subscriptionDetails]
  );

  const availableBillingIntervals = useMemo(
    () => getPlansBillingIntervals(plans),
    [plans]
  );

  const showBillingSwitcher = availableBillingIntervals.length > 1;

  const displayedPlans = useMemo(
    () => getPlansForBillingInterval(plans, billingInterval),
    [plans, billingInterval]
  );

  useEffect(() => {
    if (!plans.length) {
      return;
    }

    setBillingInterval((prev) => {
      if (userSelectedBillingInterval.current && getPlansForBillingInterval(plans, prev).length > 0) {
        return prev;
      }

      const currentInterval = currentPlan ? normalizePlanInterval(currentPlan) : null;
      if (currentInterval && availableBillingIntervals.includes(currentInterval)) {
        return currentInterval;
      }

      if (getPlansForBillingInterval(plans, prev).length > 0) {
        return prev;
      }

      if (availableBillingIntervals.includes("month")) {
        return "month";
      }

      return availableBillingIntervals[0] || "month";
    });
  }, [plans, currentPlan, availableBillingIntervals]);

  useEffect(() => {
    let isMounted = true;
    const token = getCookie("token");

    const loadPlans = async () => {
      try {
        const [items, user] = await Promise.all([
          stripeSubscriptionService.listPublicPlansCached(),
          getUser(token, currentUser, setCurrentUser),
        ]);

        if (!isMounted) {
          return;
        }

        setPlans(items);
        if (user) {
          syncSubscriptionFromUser(user);
        }
        if (onPlansLoaded) {
          onPlansLoaded({ plans: items, user });
        }
      } catch {
        if (isMounted) {
          setError("No subscription plans available.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToBillingPortal = async (failureMessage, intent = "manage") => {
    setBillingInProgress(true);
    setError("");

    try {
      const token = getCookie("token");
      const user = await getUser(token, currentUser, setCurrentUser);

      if (user) {
        syncSubscriptionFromUser(user);
      }

      const subscription = getActiveTenantSubscription(user);
      const billingPortalUrl = buildBillingPortalUrl(
        user?.tenant_subscription?.billing_portal_url,
        getSubscriptionId(subscription, user),
        intent
      );

      if (billingPortalUrl) {
        window.location.assign(billingPortalUrl);
        return;
      }

      setError(failureMessage || "Could not open the billing portal. Please try again.");
    } catch {
      setError(failureMessage || "Could not open the billing portal. Please try again.");
    } finally {
      setBillingInProgress(false);
    }
  };

  const subscribe = (plan) => {
    if (plan.is_current || isPlanDowngrade(currentPlan, plan)) {
      return;
    }

    if (hasActiveSubscription) {
      redirectToBillingPortal(
        "Could not open billing portal to update your subscription.",
        "update"
      );
      return;
    }

    setInProgress(true);
    stripeSubscriptionService
      .createCheckoutSession(plan.id)
      .then((res) => {
        if (res.url) window.location.assign(res.url);
      })
      .catch(() => {
        setError("Could not start checkout. Please try again later.");
        setInProgress(false);
      });
  };

  if (loading) {
    return hideWhenSubscribed ? null : <LoaderInner show />;
  }

  if (hideWhenSubscribed && hasActiveSubscription) {
    return null;
  }

  if (hideWhenEmpty && plans.length === 0 && !activeSubscription) {
    return null;
  }

  if (error && plans.length === 0 && !activeSubscription) {
    return hideWhenEmpty ? null : <Alert color="danger">{error}</Alert>;
  }

  return (
    <>
      {inProgress && <LoaderInner show={inProgress} />}
      {billingInProgress && <LoaderInner show={billingInProgress} />}

      <Card className="stripe-plans-available-card">
        <CardBody>
          <div className="stripe-plans-section-header">
            <CardTitle tag="h3" className="mb-0">
              {title}
            </CardTitle>
            {showManageLink && (
              <Link to="/subscription" className="stripe-plans-manage-link">
                Manage subscription
              </Link>
            )}
          </div>
          {error && <Alert color="danger">{error}</Alert>}
          {showBillingSwitcher && (
            <div
              className="stripe-plans-billing-switcher"
              role="group"
              aria-label="Billing interval"
            >
              <button
                type="button"
                className={`stripe-plans-billing-switcher-btn${
                  billingInterval === "month" ? " is-active" : ""
                }`}
                onClick={() => {
                  userSelectedBillingInterval.current = true;
                  setBillingInterval("month");
                }}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`stripe-plans-billing-switcher-btn${
                  billingInterval === "year" ? " is-active" : ""
                }`}
                onClick={() => {
                  userSelectedBillingInterval.current = true;
                  setBillingInterval("year");
                }}
              >
                Yearly
              </button>
            </div>
          )}
          <div className="stripe-plan-cards-grid">
            {displayedPlans.map((plan) => {
              const cta = getPlanCardCta(plan, { currentPlan, hasActiveSubscription });

              return (
                <div key={plan.id} className="stripe-plan-card-col">
                  <SubscriptionPlanCard
                    name={plan.name}
                    priceDisplay={plan.price_display}
                    description={plan.description}
                    features={plan.features || []}
                    entitlements={buildPlanEntitlements(plan)}
                    isCurrent={!!plan.is_current}
                    ctaLabel={cta.label}
                    ctaDisabled={cta.disabled || inProgress || billingInProgress}
                    ctaLocked={cta.locked}
                    onCtaClick={cta.disabled ? undefined : () => subscribe(plan)}
                  />
                </div>
              );
            })}
          </div>
          {plans.length === 0 && (
            <Alert color="info" className="stripe-plans-empty-alert mb-0">
              No subscription plans available.
            </Alert>
          )}
          {plans.length > 0 && displayedPlans.length === 0 && (
            <Alert color="info" className="stripe-plans-empty-alert mb-0">
              No {billingInterval === "year" ? "yearly" : "monthly"} plans available.
            </Alert>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default AvailablePlansSection;
